import { supabase } from '../lib/supabase';
import { DEFAULT_TENANT_ID } from '../lib/defaultTenant';
import { filterCoursesForDefaultTenant } from './coursesService';
import { syncCatalogCoursesToDb } from './coursesCatalogSync';
import { 
  Quote, 
  QuoteDate, 
  QuoteLineItem, 
  Invoice, 
  InvoiceLineItem, 
  Course,
  QuoteWithDetails,
  InvoiceWithDetails,
  QuotePayment
} from '../types/quotes';
import { Customer, Contact, Location } from '../types/crm';
import { ClientNotesService } from './clientNotesService';
import type { AttendeePaymentMethod, AttendeePaymentStepType } from './courseSessionsService';
import { courseSessionsService } from './courseSessionsService';

export class QuotesService {
  private static deriveTotalsFromLineItems(items: Array<{ quantity?: number; unitPrice?: number; totalPrice?: number }>): { subtotal: number; totalAmount: number } {
    const subtotal = (items || []).reduce((sum, item) => {
      const explicitTotal = Number(item.totalPrice);
      if (Number.isFinite(explicitTotal) && explicitTotal > 0) return sum + explicitTotal;
      const qty = Number(item.quantity || 0);
      const unit = Number(item.unitPrice || 0);
      return sum + (Number.isFinite(qty) && Number.isFinite(unit) ? qty * unit : 0);
    }, 0);
    return { subtotal, totalAmount: subtotal };
  }
  private static isMissingGenerateQuoteNumberFunction(error: any): boolean {
    const msg = String(error?.message || '').toLowerCase();
    return msg.includes('generate_quote_number') && msg.includes('schema cache');
  }

  private static isMissingQuotesCourseDescriptionColumn(error: any): boolean {
    const msg = String(error?.message || '').toLowerCase();
    return msg.includes('course_description') && msg.includes('quotes');
  }

  private static getMissingQuotesColumnFromError(error: any): string | null {
    const msg = String(error?.message || '');
    const direct = /Could not find the '([^']+)' column of 'quotes'/i.exec(msg);
    if (direct?.[1]) return direct[1];
    const pg = /column\s+quotes\.([a-zA-Z0-9_]+)\s+does not exist/i.exec(msg);
    if (pg?.[1]) return pg[1];
    return null;
  }

  private static getMissingTableColumnFromError(error: any, tableName: string): string | null {
    const msg = String(error?.message || '');
    const direct = new RegExp(`Could not find the '([^']+)' column of '${tableName}'`, 'i').exec(msg);
    if (direct?.[1]) return direct[1];
    const pg = new RegExp(`column\\s+${tableName}\\.([a-zA-Z0-9_]+)\\s+does not exist`, 'i').exec(msg);
    if (pg?.[1]) return pg[1];
    return null;
  }

  private static async insertQuoteWithSchemaFallback(initialRow: Record<string, any>): Promise<any> {
    let row = { ...initialRow };
    for (let i = 0; i < 8; i += 1) {
      const res = await supabase.from('quotes').insert(row).select().single();
      if (!res.error) return res.data;
      const missingCol = this.getMissingQuotesColumnFromError(res.error);
      if (!missingCol || !(missingCol in row)) throw res.error;
      const { [missingCol]: _omit, ...nextRow } = row;
      row = nextRow;
    }
    throw new Error('Could not save quote due to repeated schema mismatches.');
  }

  private static async updateQuoteWithSchemaFallback(id: string, initialUpdates: Record<string, any>): Promise<any> {
    let updates = { ...initialUpdates };
    for (let i = 0; i < 8; i += 1) {
      const res = await supabase.from('quotes').update(updates).eq('id', id).select().single();
      if (!res.error) return res.data;
      const missingCol = this.getMissingQuotesColumnFromError(res.error);
      if (!missingCol || !(missingCol in updates)) throw res.error;
      const { [missingCol]: _omit, ...nextUpdates } = updates;
      updates = nextUpdates;
    }
    throw new Error('Could not update quote due to repeated schema mismatches.');
  }

  private static async insertQuoteLineItemsWithSchemaFallback(initialRows: Record<string, any>[]): Promise<any[]> {
    let rows = initialRows.map((r) => ({ ...r }));
    for (let i = 0; i < 10; i += 1) {
      const res = await supabase.from('quote_line_items').insert(rows).select();
      if (!res.error) return res.data || [];
      const missingCol = this.getMissingTableColumnFromError(res.error, 'quote_line_items');
      if (!missingCol) throw res.error;
      let removed = false;
      rows = rows.map((row) => {
        if (missingCol in row) {
          removed = true;
          const { [missingCol]: _omit, ...next } = row;
          return next;
        }
        return row;
      });
      if (!removed) throw res.error;
    }
    throw new Error('Could not save quote line items due to repeated schema mismatches.');
  }

  private static async generateQuoteNumberFallback(): Promise<string> {
    const { data, error } = await supabase
      .from('quotes')
      .select('quote_number')
      .order('created_at', { ascending: false })
      .limit(200);
    if (error) throw error;

    let maxN = 0;
    for (const row of data || []) {
      const raw = String((row as any).quote_number || '');
      const match = raw.match(/(\d+)/g);
      if (!match || match.length === 0) continue;
      const n = Number(match[match.length - 1]);
      if (Number.isFinite(n) && n > maxN) maxN = n;
    }
    return `Q${String(maxN + 1).padStart(5, '0')}`;
  }

  private static async generateQuoteNumberSafe(): Promise<string> {
    const { data: quoteNumber, error: numberError } = await supabase.rpc('generate_quote_number');
    if (!numberError && quoteNumber) return String(quoteNumber);
    if (this.isMissingGenerateQuoteNumberFunction(numberError)) {
      return this.generateQuoteNumberFallback();
    }
    if (numberError) throw numberError;
    return this.generateQuoteNumberFallback();
  }

  // Course Management
  static async getCourses(): Promise<Course[]> {
    await syncCatalogCoursesToDb().catch(() => undefined);
    const { data, error } = await supabase.from('courses').select('*').order('name');
    if (error) throw error;

    const filtered = filterCoursesForDefaultTenant((data ?? []) as Record<string, unknown>[]);

    // Map database column names to TypeScript interface
    return filtered.map((course: any) => ({
      id: course.id,
      name: course.name,
      description: course.description,
      basePrice: course.base_price,
      validityMonths: course.validity_months,
      isActive: course.is_active,
      createdAt: course.created_at,
      updatedAt: course.updated_at
    }));
  }

  static async getCourseByName(name: string): Promise<Course | null> {
    await syncCatalogCoursesToDb().catch(() => undefined);
    const byNameAttempts = [
      () =>
        supabase
          .from('courses')
          .select('*')
          .eq('name', name)
          .eq('is_active', true)
          .maybeSingle(),
      () => supabase.from('courses').select('*').eq('name', name).maybeSingle(),
    ];
    let data: Record<string, unknown> | null = null;
    let error: { code?: string; message?: string } | null = null;
    for (const run of byNameAttempts) {
      const res = await run();
      data = res.data as Record<string, unknown> | null;
      error = res.error;
      if (!error) break;
      if (error.code === '42703') continue;
      if (error.code !== 'PGRST116') throw error;
      break;
    }
    if (error && error.code !== 'PGRST116') throw error;
    
    if (!data) return null;

    const course = data as Record<string, unknown>;
    // Map database column names to TypeScript interface
    return {
      id: String(course.id ?? ''),
      name: String(course.name ?? ''),
      description: course.description != null ? String(course.description) : undefined,
      basePrice: Number(course.base_price ?? 0),
      validityMonths: Number(course.validity_months ?? 0),
      isActive: Boolean(course.is_active ?? true),
      createdAt: String(course.created_at ?? ''),
      updatedAt: String(course.updated_at ?? '')
    };
  }

  // Quote Management
  static async createQuote(quote: Omit<Quote, 'id' | 'quoteNumber' | 'createdAt' | 'updatedAt'>): Promise<Quote> {
    const quoteNumber = await this.generateQuoteNumberSafe();

    const dbQuote = {
      tenant_id: DEFAULT_TENANT_ID,
      customer_id: quote.customerId,
      contact_id: quote.contactId,
      location_id: quote.locationId,
      course_name: quote.courseName,
      course_description: quote.courseDescription,
      subtotal: quote.subtotal,
      total_amount: quote.totalAmount,
      instructor_price: quote.instructorPrice,
      instructor_po: quote.instructorPO,
      client_po: quote.clientPO,
      status: quote.status,
      valid_until: quote.validUntil,
      notes: quote.notes,
      accepted_date: quote.acceptedDate,
      accepted_by: quote.acceptedBy,
      created_by: quote.createdBy,
      quote_number: quoteNumber,
      source: quote.source ?? null,
      course_session_id: quote.courseSessionId ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    return this.insertQuoteWithSchemaFallback(dbQuote as Record<string, any>);
  }

  static async getQuotes(): Promise<Quote[]> {
    // Check authentication
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Single-tenant system: all authenticated users can see all quotes
    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  }

  static async getQuotesWithDetails(): Promise<QuoteWithDetails[]> {
    // Check authentication
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Single-tenant system: all authenticated users can see all quotes
    const { data, error } = await supabase
      .from('quotes')
      .select(`
        *,
        customer:customers(*, locations(*)),
        contact:contacts(*),
        location:locations(*),
        dates:quote_dates(*),
        lineItems:quote_line_items(*)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async getQuoteByNumber(quoteNumber: string): Promise<any> {
    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .eq('quote_number', quoteNumber)
      .single();
    
    if (error) throw error;
    
    return data;
  }

  static async getQuoteByNumberWithDetails(quoteNumber: string): Promise<QuoteWithDetails | null> {
    const { data, error } = await supabase
      .from('quotes')
      .select(`
        *,
        customer:customers(*, locations(*)),
        contact:contacts(*),
        location:locations(*),
        dates:quote_dates(*),
        lineItems:quote_line_items(*)
      `)
      .eq('quote_number', quoteNumber)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  static async getQuoteById(id: string): Promise<QuoteWithDetails | null> {
    const { data, error } = await supabase
      .from('quotes')
      .select(`
        *,
        customer:customers(*, locations(*)),
        contact:contacts(*),
        location:locations(*),
        dates:quote_dates(*),
        lineItems:quote_line_items(*)
      `)
      .eq('id', id)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  static async updateQuoteStatus(id: string, status: Quote['status'], acceptedBy?: string): Promise<void> {
    // Get current quote to find customer and quote number
    const { data: currentQuote, error: fetchError } = await supabase
      .from('quotes')
      .select('customer_id, quote_number, status')
      .eq('id', id)
      .single();
    
    if (fetchError) throw fetchError;
    if (!currentQuote) throw new Error('Quote not found');

    const updateData: Record<string, any> = {
      status,
      updated_at: new Date().toISOString()
    };

    if (status === 'awaiting_payment') {
      updateData.payment_status = 'unpaid';
      if (acceptedBy) {
        updateData.accepted_date = new Date().toISOString();
        updateData.accepted_by = acceptedBy;
      }
    } else if (status === 'accepted') {
      if (currentQuote.status !== 'awaiting_payment') {
        updateData.accepted_date = new Date().toISOString();
        if (acceptedBy) updateData.accepted_by = acceptedBy;
      } else {
        // Staff moved quote to accepted without using payment fields — clear quote payment tracking
        updateData.payment_status = null;
      }
    }

    if (status === 'declined') {
      updateData.payment_status = null;
    }

    await this.updateQuoteWithSchemaFallback(id, updateData);

    // Add note to customer about status change
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userName = user?.user_metadata?.first_name && user?.user_metadata?.last_name
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
        : user?.email?.split('@')[0] || 'System';
      
      const statusLabels: Record<string, string> = {
        'draft': 'Draft',
        'sent': 'Sent',
        'awaiting_payment': 'Waiting list',
        'accepted': 'Accepted',
        'declined': 'Declined',
        'expired': 'Expired'
      };
      
      const oldStatusLabel = statusLabels[currentQuote.status] || currentQuote.status;
      const newStatusLabel = statusLabels[status] || status;
      
      const noteText = `Quote #${currentQuote.quote_number} status changed from ${oldStatusLabel} to ${newStatusLabel}${(status === 'accepted' || status === 'awaiting_payment') && acceptedBy ? ` by ${acceptedBy}` : ''}.`;
      
      await ClientNotesService.addClientNote(
        currentQuote.customer_id,
        noteText,
        user?.id,
        userName
      );
    } catch (noteError) {
      // Log error but don't fail the status update
      console.error('Failed to add note for quote status change:', noteError);
    }

    await this.syncLinkedCourseSessionAttendeeForQuoteId(id);
  }

  /** Record deposit / full payment for quotes on the waiting list; promotes to accepted when paid or part-paid. */
  static async updateQuotePaymentStatus(
    id: string,
    paymentStatus: NonNullable<Quote['paymentStatus']>
  ): Promise<void> {
    const { data: currentQuote, error: fetchError } = await supabase
      .from('quotes')
      .select('customer_id, quote_number, status')
      .eq('id', id)
      .single();

    if (fetchError) throw fetchError;
    if (!currentQuote) throw new Error('Quote not found');

    const updateData: Record<string, any> = {
      payment_status: paymentStatus,
      updated_at: new Date().toISOString()
    };

    if (currentQuote.status === 'awaiting_payment' && (paymentStatus === 'half_paid' || paymentStatus === 'paid')) {
      updateData.status = 'accepted';
    }

    await this.updateQuoteWithSchemaFallback(id, updateData);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userName = user?.user_metadata?.first_name && user?.user_metadata?.last_name
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
        : user?.email?.split('@')[0] || 'System';

      const payLabel =
        paymentStatus === 'paid' ? 'Paid in full' : paymentStatus === 'half_paid' ? 'Deposit / part paid' : 'Unpaid';

      const noteText = `Quote #${currentQuote.quote_number} payment status set to ${payLabel}.`;

      await ClientNotesService.addClientNote(
        currentQuote.customer_id,
        noteText,
        user?.id,
        userName
      );
    } catch (noteError) {
      console.error('Failed to add note for quote payment update:', noteError);
    }

    await this.syncLinkedCourseSessionAttendeeForQuoteId(id);
  }

  private static grossAmountFromQuoteRow(quote: Record<string, any>): number {
    const direct = Number(quote?.total_amount || quote?.totalAmount || quote?.subtotal || 0);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const rawItems = quote?.lineItems || quote?.line_items || [];
    const items = Array.isArray(rawItems) ? rawItems : [];
    return items.reduce((sum: number, item: any) => {
      const total = Number(item?.total_price ?? item?.totalPrice ?? 0);
      if (Number.isFinite(total) && total > 0) return sum + total;
      const qty = Number(item?.quantity || 0);
      const unit = Number(item?.unit_price ?? item?.unitPrice ?? 0);
      if (Number.isFinite(qty) && Number.isFinite(unit) && qty > 0 && unit > 0) return sum + qty * unit;
      return sum;
    }, 0);
  }

  private static async syncLinkedCourseSessionAttendeeForQuoteSnapshot(quote: QuoteWithDetails): Promise<void> {
    try {
      if (!quote?.customer) return;
      const sessionId = (quote as any).course_session_id ?? quote.courseSessionId ?? null;
      if (!sessionId || typeof sessionId !== 'string') return;

      let paidTotal = 0;
      try {
        const payments = await this.listQuotePayments(quote.id);
        paidTotal = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
      } catch {
        /* ignore — e.g. public quote page without quote_payments RLS */
      }

      const quoteStatus = String((quote as any).status ?? quote.status ?? '');
      const paymentStatus = ((quote as any).payment_status ?? quote.paymentStatus ?? null) as string | null;
      const quoteNumber = String((quote as any).quote_number || quote.quoteNumber || '');
      const fee = this.grossAmountFromQuoteRow(quote as any);
      const feeOrNull = Number.isFinite(fee) && fee > 0 ? fee : null;

      await courseSessionsService.syncAttendeeFromLinkedQuote({
        quoteId: quote.id,
        sessionId,
        quoteNumber,
        quoteStatus,
        paymentStatus,
        customer: quote.customer,
        contact: quote.contact || null,
        quoteSource: ((quote as any).source ?? quote.source) as import('../types').InboundChannel | null,
        enrollmentCourseFeeEuro: feeOrNull,
        amountPaidEuro: paidTotal
      });
    } catch (e) {
      console.error('syncLinkedCourseSessionAttendeeForQuoteSnapshot', e);
    }
  }

  /** Use when the full quote is already loaded (e.g. public accept page where `getQuoteById` may be RLS-blocked). */
  static async syncLinkedCourseSessionAttendeeFromLoadedQuote(quote: QuoteWithDetails): Promise<void> {
    await this.syncLinkedCourseSessionAttendeeForQuoteSnapshot(quote);
  }

  private static async syncLinkedCourseSessionAttendeeForQuoteId(quoteId: string): Promise<void> {
    const quote = await this.getQuoteById(quoteId);
    if (!quote) return;
    await this.syncLinkedCourseSessionAttendeeForQuoteSnapshot(quote);
  }

  static async listQuotePayments(quoteId: string): Promise<QuotePayment[]> {
    const { data, error } = await supabase
      .from('quote_payments')
      .select('*')
      .eq('quote_id', quoteId)
      .order('payment_date', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) {
      const msg = String(error.message || '').toLowerCase();
      if (error.code === '42P01' || msg.includes('quote_payments')) return [];
      throw error;
    }
    return (data || []).map((row: any) => ({
      id: row.id,
      quoteId: row.quote_id,
      amount: Number(row.amount || 0),
      method: String(row.method || 'cash'),
      paymentDate: row.payment_date,
      stepType: String(row.step_type || 'other'),
      payerNotes: row.payer_notes || null,
      createdAt: row.created_at
    }));
  }

  /** Recompute `quotes.payment_status` from `quote_payments` rows; promotes waiting list to accepted when appropriate. */
  static async syncQuotePaymentStatusFromLedger(quoteId: string): Promise<void> {
    const quote = await this.getQuoteById(quoteId);
    if (!quote) return;
    const payments = await this.listQuotePayments(quoteId);
    const paid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const total = this.grossAmountFromQuoteRow(quote as any);

    let payment_status: NonNullable<Quote['paymentStatus']> = 'unpaid';
    if (total > 0 && paid >= total - 0.005) payment_status = 'paid';
    else if (paid > 0) payment_status = 'half_paid';

    const currentStatus = (quote as any).status ?? quote.status;
    const updateData: Record<string, any> = {
      payment_status,
      updated_at: new Date().toISOString()
    };
    if (currentStatus === 'awaiting_payment' && (payment_status === 'half_paid' || payment_status === 'paid')) {
      updateData.status = 'accepted';
    }
    await this.updateQuoteWithSchemaFallback(quoteId, updateData);
    await this.syncLinkedCourseSessionAttendeeForQuoteId(quoteId);
  }

  static async addQuotePayment(args: {
    quoteId: string;
    amount: number;
    method: AttendeePaymentMethod;
    paymentDate: string;
    stepType?: AttendeePaymentStepType;
    payerNotes?: string | null;
  }): Promise<void> {
    const { data: meta, error: metaErr } = await supabase
      .from('quotes')
      .select('quote_number, customer_id')
      .eq('id', args.quoteId)
      .single();
    if (metaErr) throw metaErr;
    if (!meta) throw new Error('Quote not found');

    const { error } = await supabase.from('quote_payments').insert({
      quote_id: args.quoteId,
      amount: args.amount,
      method: args.method,
      payment_date: args.paymentDate,
      step_type: args.stepType || 'other',
      payer_notes: args.payerNotes || null
    });
    if (error) throw error;

    await this.syncQuotePaymentStatusFromLedger(args.quoteId);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userName = user?.user_metadata?.first_name && user?.user_metadata?.last_name
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
        : user?.email?.split('@')[0] || 'System';
      const noteText = `Quote #${meta.quote_number} payment recorded: €${args.amount.toFixed(2)} (${args.method}).`;
      await ClientNotesService.addClientNote(meta.customer_id, noteText, user?.id, userName);
    } catch (noteError) {
      console.error('Failed to add note for quote payment:', noteError);
    }
  }

  static async deleteQuote(id: string): Promise<void> {
    try {
      const quote = await this.getQuoteById(id);
      const sessionId = quote ? ((quote as any).course_session_id ?? quote.courseSessionId) : null;
      if (sessionId && typeof sessionId === 'string') {
        await courseSessionsService.removeLinkedQuoteAttendee(sessionId, id);
      }
    } catch (e) {
      console.error('deleteQuote session cleanup', e);
    }

    const { error } = await supabase
      .from('quotes')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }

  static async updateQuote(
    id: string,
    updates: Partial<Omit<Quote, 'id' | 'quoteNumber' | 'createdAt' | 'createdBy'>>
  ): Promise<Quote> {
    // Map camelCase to snake_case for database
    const dbUpdates: any = {
      updated_at: new Date().toISOString()
    };

    if (updates.customerId !== undefined) dbUpdates.customer_id = updates.customerId;
    if (updates.contactId !== undefined) dbUpdates.contact_id = updates.contactId;
    if (updates.locationId !== undefined) dbUpdates.location_id = updates.locationId;
    if (updates.courseName !== undefined) dbUpdates.course_name = updates.courseName;
    if (updates.courseDescription !== undefined) dbUpdates.course_description = updates.courseDescription;
    if (updates.subtotal !== undefined) dbUpdates.subtotal = updates.subtotal;
    if (updates.totalAmount !== undefined) dbUpdates.total_amount = updates.totalAmount;
    if (updates.instructorPrice !== undefined) dbUpdates.instructor_price = updates.instructorPrice;
    if (updates.instructorPO !== undefined) dbUpdates.instructor_po = updates.instructorPO;
    if (updates.clientPO !== undefined) dbUpdates.client_po = updates.clientPO;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.paymentStatus !== undefined) dbUpdates.payment_status = updates.paymentStatus;
    if (updates.validUntil !== undefined) dbUpdates.valid_until = updates.validUntil;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
    if (updates.acceptedDate !== undefined) dbUpdates.accepted_date = updates.acceptedDate;
    if (updates.acceptedBy !== undefined) dbUpdates.accepted_by = updates.acceptedBy;
    if (updates.source !== undefined) dbUpdates.source = updates.source;
    if (updates.courseSessionId !== undefined) {
      dbUpdates.course_session_id = updates.courseSessionId;
    }

    const out = await this.updateQuoteWithSchemaFallback(id, dbUpdates as Record<string, any>);
    await this.syncLinkedCourseSessionAttendeeForQuoteId(id);
    return out;
  }

  // Quote Dates
  static async createQuoteDates(dates: Omit<QuoteDate, 'id' | 'createdAt'>[]): Promise<QuoteDate[]> {
    // Map camelCase to snake_case for database
    const dbDates = dates.map(date => ({
      quote_id: date.quoteId,
      date: date.date,
      start_time: date.startTime,
      end_time: date.endTime,
      is_preferred: date.isPreferred,
      created_at: new Date().toISOString()
    }));

    const { data, error } = await supabase
      .from('quote_dates')
      .insert(dbDates)
      .select();
    
    if (error) throw error;
    return data || [];
  }

  static async updateQuoteDates(quoteId: string, dates: Omit<QuoteDate, 'id' | 'createdAt'>[]): Promise<QuoteDate[]> {
    // Delete existing dates for this quote
    const { error: deleteError } = await supabase
      .from('quote_dates')
      .delete()
      .eq('quote_id', quoteId);
    
    if (deleteError) throw deleteError;

    // Create new dates
    if (dates.length > 0) {
      return await this.createQuoteDates(dates);
    }
    
    return [];
  }

  static async deleteQuoteDates(quoteId: string): Promise<void> {
    const { error } = await supabase
      .from('quote_dates')
      .delete()
      .eq('quote_id', quoteId);
    
    if (error) throw error;
  }

  // Quote Line Items
  static async createQuoteLineItems(items: Omit<QuoteLineItem, 'id' | 'createdAt'>[]): Promise<QuoteLineItem[]> {
    // Helper function to check if a string is a valid UUID
    const isValidUUID = (str: string | undefined | null): boolean => {
      if (!str) return false;
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return uuidRegex.test(str);
    };

    // Map camelCase to snake_case for database
    // course_id is optional - only include if it's a valid UUID
    // COURSE_CONFIG courses have slug IDs (e.g., "gator") not UUIDs, so we omit course_id for them
    const dbItems = items.map(item => {
      const dbItem: any = {
        quote_id: item.quoteId,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        total_price: item.totalPrice,
        created_at: new Date().toISOString()
      };
      
      // Only include course_id if it's a valid UUID
      // This filters out COURSE_CONFIG course IDs which are slugs like "gator"
      if (item.courseId && isValidUUID(item.courseId)) {
        dbItem.course_id = item.courseId;
      }
      
      return dbItem;
    });

    return this.insertQuoteLineItemsWithSchemaFallback(dbItems as Record<string, any>[]);
  }

  static async updateQuoteLineItems(quoteId: string, items: Omit<QuoteLineItem, 'id' | 'createdAt'>[]): Promise<QuoteLineItem[]> {
    // Delete existing line items for this quote
    const { error: deleteError } = await supabase
      .from('quote_line_items')
      .delete()
      .eq('quote_id', quoteId);
    
    if (deleteError) throw deleteError;

    // Create new line items
    if (items.length > 0) {
      return await this.createQuoteLineItems(items);
    }
    
    return [];
  }

  static async deleteQuoteLineItems(quoteId: string): Promise<void> {
    const { error } = await supabase
      .from('quote_line_items')
      .delete()
      .eq('quote_id', quoteId);
    
    if (error) throw error;
  }

  // Invoice Management
  static async createInvoiceFromQuote(quoteId: string, dueDate: string): Promise<Invoice> {
    // Get quote details
    const quote = await this.getQuoteById(quoteId);
    if (!quote) throw new Error('Quote not found');

    // Generate invoice number
    const { data: invoiceNumber, error: numberError } = await supabase
      .rpc('generate_invoice_number');
    
    if (numberError) throw numberError;

    const invoice: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'> & { tenant_id: string } = {
      tenant_id: DEFAULT_TENANT_ID,
      quoteId,
      customerId: quote.customerId,
      invoiceNumber,
      subtotal: quote.subtotal,
      totalAmount: quote.totalAmount,
      dueDate,
      status: 'draft'
    };

    const { data, error } = await supabase
      .from('invoices')
      .insert({
        tenant_id: invoice.tenant_id,
        quote_id: invoice.quoteId,
        customer_id: invoice.customerId,
        invoice_number: invoice.invoiceNumber,
        subtotal: invoice.subtotal,
        total_amount: invoice.totalAmount,
        due_date: invoice.dueDate,
        status: invoice.status,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }

  static async getInvoices(): Promise<Invoice[]> {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  }

  // Convert accepted quote to booking
  static async convertQuoteToBooking(quoteId: string, selectedDateId: string): Promise<string> {
    const quote = await this.getQuoteById(quoteId);
    if (!quote) throw new Error('Quote not found');

    const quoteStatus = (quote as any).status ?? quote.status;
    if (quoteStatus !== 'accepted') {
      throw new Error(
        'Only accepted quotes can be converted to a booking. If this quote is on the waiting list, record deposit or payment first.'
      );
    }

    // Support both snake_case (from DB) and camelCase (from TypeScript). Pass quote location to booking.
    const courseName = (quote as any).course_name || quote.courseName;
    const customerId = (quote as any).customer_id || quote.customerId;
    const locationId = (quote as any).location_id ?? quote.locationId ?? (quote as any).location?.id ?? quote.location?.id;
    const contactId = (quote as any).contact_id || quote.contactId;
    const totalAmount = (quote as any).total_amount || quote.totalAmount;
    const quoteNumber = (quote as any).quote_number || quote.quoteNumber;
    const instructorPrice = (quote as any).instructor_price || quote.instructorPrice;
    const instructorPO = (quote as any).instructor_po || quote.instructorPO;
    const clientPO = (quote as any).client_po || quote.clientPO;
    const courseDescription = (quote as any).course_description || quote.courseDescription;
    const quoteNotes = (quote as any).notes || quote.notes;

    if (!courseName) {
      throw new Error('Quote course name is missing. Cannot convert to booking.');
    }

    const selectedDate = quote.dates.find(d => d.id === selectedDateId);
    if (!selectedDate) throw new Error('Selected date not found');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Build notes combining quote notes, course description, and conversion info
    const notesParts: string[] = [];
    if (quoteNotes) notesParts.push(quoteNotes);
    if (courseDescription) notesParts.push(`Course Description: ${courseDescription}`);
    notesParts.push(`Converted from quote ${quoteNumber}`);
    const combinedNotes = notesParts.join('\n\n');

    // Create booking from quote
    const { data, error } = await supabase
      .from('bookings')
      .insert({
        tenant_id: DEFAULT_TENANT_ID,
        customer_id: customerId,
        location_id: locationId || null,
        contact_id: contactId,
        course_name: courseName,
        course_date: selectedDate.date,
        start_time: selectedDate.startTime || null,
        end_time: selectedDate.endTime || null,
        total_price: totalAmount || null,
        instructor_price: instructorPrice || null,
        instructor_po: instructorPO || null,
        client_po: clientPO || null,
        attendee_count: 1, // Default to 1 attendee when converting from quote
        booking_type: 'standard', // Default to standard booking type
        notes: combinedNotes,
        status: 'confirmed'
      })
      .select('id')
      .single();
    
    if (error) throw error;
    return data?.id as string;
  }

  // Convert accepted quote to booking with manual date
  static async convertQuoteToBookingWithManualDate(
    quoteId: string,
    manualDate: string,
    startTime?: string,
    endTime?: string
  ): Promise<string> {
    const quote = await this.getQuoteById(quoteId);
    if (!quote) throw new Error('Quote not found');

    const quoteStatus = (quote as any).status ?? quote.status;
    if (quoteStatus !== 'accepted') {
      throw new Error(
        'Only accepted quotes can be converted to a booking. If this quote is on the waiting list, record deposit or payment first.'
      );
    }

    // Support both snake_case (from DB) and camelCase (from TypeScript). Pass quote location to booking.
    const courseName = (quote as any).course_name || quote.courseName;
    const customerId = (quote as any).customer_id || quote.customerId;
    const locationId = (quote as any).location_id ?? quote.locationId ?? (quote as any).location?.id ?? quote.location?.id;
    const contactId = (quote as any).contact_id || quote.contactId;
    const totalAmount = (quote as any).total_amount || quote.totalAmount;
    const quoteNumber = (quote as any).quote_number || quote.quoteNumber;
    const instructorPrice = (quote as any).instructor_price || quote.instructorPrice;
    const instructorPO = (quote as any).instructor_po || quote.instructorPO;
    const clientPO = (quote as any).client_po || quote.clientPO;
    const courseDescription = (quote as any).course_description || quote.courseDescription;
    const quoteNotes = (quote as any).notes || quote.notes;

    if (!courseName) {
      throw new Error('Quote course name is missing. Cannot convert to booking.');
    }

    if (!manualDate) {
      throw new Error('Date is required.');
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Build notes combining quote notes, course description, and conversion info
    const notesParts: string[] = [];
    if (quoteNotes) notesParts.push(quoteNotes);
    if (courseDescription) notesParts.push(`Course Description: ${courseDescription}`);
    notesParts.push(`Converted from quote ${quoteNumber}`);
    const combinedNotes = notesParts.join('\n\n');

    // Create booking from quote with manual date
    const { data, error } = await supabase
      .from('bookings')
      .insert({
        tenant_id: DEFAULT_TENANT_ID,
        customer_id: customerId,
        location_id: locationId || null,
        contact_id: contactId,
        course_name: courseName,
        course_date: manualDate,
        start_time: startTime || null,
        end_time: endTime || null,
        total_price: totalAmount || null,
        instructor_price: instructorPrice || null,
        instructor_po: instructorPO || null,
        client_po: clientPO || null,
        attendee_count: 1, // Default to 1 attendee when converting from quote
        booking_type: 'standard', // Default to standard booking type
        notes: combinedNotes,
        status: 'confirmed'
      })
      .select('id')
      .single();
    
    if (error) throw error;
    return data?.id as string;
  }

  // Helper method to create complete quote with dates and line items
  static async createCompleteQuote(
    quoteData: Omit<Quote, 'id' | 'quoteNumber' | 'createdAt' | 'updatedAt'> & {
      customer: Customer;
      contact: Contact;
      location?: Location;
    },
    dates: Omit<QuoteDate, 'id' | 'quoteId' | 'createdAt'>[],
    lineItems: Omit<QuoteLineItem, 'id' | 'quoteId' | 'createdAt'>[]
  ): Promise<QuoteWithDetails> {
    const derivedTotals = this.deriveTotalsFromLineItems(lineItems as any[]);
    const normalizedQuoteData = {
      ...quoteData,
      subtotal: derivedTotals.subtotal,
      totalAmount: derivedTotals.totalAmount
    };

    // Create quote
    const quote = await this.createQuote(normalizedQuoteData);
    
    // Create dates
    const quoteDates = await this.createQuoteDates(
      dates.map(date => ({ ...date, quoteId: quote.id }))
    );
    
    // Create line items
    const quoteLineItems = await this.createQuoteLineItems(
      lineItems.map(item => ({ ...item, quoteId: quote.id }))
    );
    
    // Add note to customer about quote creation
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userName = user?.user_metadata?.first_name && user?.user_metadata?.last_name
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
        : user?.email?.split('@')[0] || 'System';
      
      const statusLabel = quote.status === 'draft' ? 'Draft' : quote.status.charAt(0).toUpperCase() + quote.status.slice(1);
      const totalAmount = quote.totalAmount || quote.subtotal || 0;
      const courseName = quote.courseName || 'Training Course';
      
      const quoteNumber = (quote as any).quote_number || quote.quoteNumber || 'N/A';
      const noteText = `Quote #${quoteNumber} created (${statusLabel}) for ${courseName} - Total: €${totalAmount.toFixed(2)}${quote.validUntil ? `, Valid until: ${new Date(quote.validUntil).toLocaleDateString('en-GB')}` : ''}.`;
      
      await ClientNotesService.addClientNote(
        quoteData.customerId,
        noteText,
        user?.id,
        userName
      );
    } catch (noteError) {
      // Log error but don't fail the quote creation
      console.error('Failed to add note for quote creation:', noteError);
    }
    
    // Return complete quote
    return {
      ...quote,
      customer: quoteData.customer,
      contact: quoteData.contact,
      location: quoteData.location,
      dates: quoteDates,
      lineItems: quoteLineItems
    };
  }

  // Helper method to update complete quote with dates and line items
  static async updateCompleteQuote(
    quoteId: string,
    quoteData: Partial<Omit<Quote, 'id' | 'quoteNumber' | 'createdAt' | 'createdBy'>> & {
      customer?: Customer;
      contact?: Contact;
      location?: Location;
    },
    dates?: Omit<QuoteDate, 'id' | 'quoteId' | 'createdAt'>[],
    lineItems?: Omit<QuoteLineItem, 'id' | 'quoteId' | 'createdAt'>[]
  ): Promise<QuoteWithDetails> {
    const normalizedQuoteData = { ...quoteData };
    if (lineItems !== undefined) {
      const derivedTotals = this.deriveTotalsFromLineItems(lineItems as any[]);
      normalizedQuoteData.subtotal = derivedTotals.subtotal;
      normalizedQuoteData.totalAmount = derivedTotals.totalAmount;
    }

    // Update quote
    await this.updateQuote(quoteId, normalizedQuoteData);
    
    // Update dates if provided
    if (dates !== undefined) {
      await this.updateQuoteDates(quoteId, dates.map(date => ({ ...date, quoteId })));
    }
    
    // Update line items if provided
    if (lineItems !== undefined) {
      await this.updateQuoteLineItems(quoteId, lineItems.map(item => ({ ...item, quoteId })));
    }
    
    // Get full quote with details
    const fullQuote = await this.getQuoteById(quoteId);
    if (!fullQuote) throw new Error('Quote not found after update');
    
    return fullQuote;
  }

}
